<template>
    <div>
      <Head title="Cutting Output Edit" />
      <div class="flex items-center justify-between mb-6">
        <h1 class="mb-8 text-3xl font-bold">
            <Link class="text-orange-400 hover:text-orange-600" href="/blocks/cutting">Cutting Output</Link>
            <span class="text-orange-400 font-medium">/</span> {{ block.name }}/Edit
        </h1>
      </div>
      <div class="bg-white rounded-md shadow mb-3">
        <form @submit.prevent="update" @keydown.enter="$event.preventDefault()"  @keyup.enter="$event.preventDefault()">
          <div class="flex flex-wrap -mb-2 -mr-6 p-8 w-full lg:w-full">
            <div v-if="form.errors.total_cut_weight && form.errors.cutting_output_volume" class="flex items-center justify-between mb-8 max-w-3xl bg-red-400 rounded w-full lg:w-full">
              <div class="flex items-center">
                <div class="py-4 text-white text-sm font-medium">
                  <span class="ml-3">Wrong SKU/Block Weight or Volume </span>
                </div>
              </div>
            </div>
            <div v-if="form.errors.total_cut_weight && !form.errors.cutting_output_volume" class="flex items-center justify-between mb-8 max-w-3xl bg-red-400 rounded w-full lg:w-full">
              <div class="flex items-center">
                <div class="py-4 text-white text-sm font-medium">
                  <span class="ml-3">{{ form.errors.total_cut_weight }}</span>
                </div>
              </div>
            </div>
            <div v-if="form.errors.cutting_output_volume && !form.errors.total_cut_weight" class="flex items-center justify-between mb-8 max-w-3xl bg-red-400 rounded w-full lg:w-full">
              <div class="flex items-center">
                <div class="py-4 text-white text-sm font-medium">
                  <span class="ml-3">{{ form.errors.cutting_output_volume }}</span>
                </div>
              </div>
            </div>
            <div class="pb-2 pr-6 mb-3 w-full lg:w-full">
              <Checkbox label="Reject" :active="is_rejected" @input="is_rejected = $event"/>
            </div>
            <div class="pb-2 pr-6 w-full lg:w-full">
              <text-input
                v-model="form.total_cut_weight"
                placeholder="total cut weight"
                class="pb-2 hidden"
              />
              <text-input
                v-model="form.cutting_output_volume"
                placeholder="total cut volume"
                class="pb-2 hidden"
              />
            </div>
            <div :class="is_rejected ? 'pb-2 pr-6 w-full lg:w-2/3' : 'pb-2 pr-6 w-full lg:w-1/3'">
              <text-input
                v-model="form.block_number"
                :error="form.errors.block_name"
                placeholder="Block Number"
                class="pb-2"
              />
            </div>
            <div class="pb-2 pr-6 w-full lg:w-1/3">
              <text-input
                v-model="form.dry_block_weight"
                :error="form.errors.dry_block_weight"
                placeholder="Dry Block Weight"
                class="pb-2"
              />
            </div>
            <div v-show="!is_rejected" class="pb-2 pr-6 w-full lg:w-1/3">
              <select-input
                v-model="form.line"
                :error="form.errors.line"
                class="pb-2"
              >
                <option :value="null">
                  Lines
                </option>
                <option
                  v-for="line in lines"
                  :key="line.id"
                  :value="line.id"
                >
                  {{ line.name }}
                </option>
              </select-input>
            </div>
            <div
              v-if="!is_rejected"
              class="w-full"
            >
              <div
                v-for="(cutting, index) in form.cuttings"
                :key="index"
                class="grid grid-cols-5"
              >
                <div class="pb-2 pr-6 w-full lg:w-4/4">
                  <text-input
                    :key="index"
                    class="hidden"
                    v-model="cutting.output_id"
                  />
                  <text-input
                    :key="index"
                    v-model="cutting.item_id"
                    :error="form.errors.item_id"
                    class="pb-2"
                    placeholder="Scan Output Part No"
                    @keyup="checkUID"
                  />
                  <span
                    v-if="(skuObj[`cuttings.${index}.message`] !='' || skuObj[`cuttings.${index}.hide`] =='error') && isValidationErrors(`cuttings.${index}.item_id`)"
                    class="mt-3"
                  >
                    <span class="form-error">{{ showValidationErrors(`cuttings.${index}.item_id`) }}</span>
                  </span>
                </div>
                <div class="pb-2 pr-6 w-full lg:w-4/4">
                  <text-input
                    :key="index"
                    class="pb-2"
                    v-model="skuObj[`cuttings.${index}.sku_size`]"
                    placeholder="Size"
                  />
                  <span
                    v-if="userSearching && skuObj[`cuttings.${index}.message`] !=''"
                    class="mt-3"
                  >
                    <span class="form-error">{{ skuObj[`cuttings.${index}.message`] }}</span>
                  </span>
                  <span v-else>
                  </span>
                </div>
                <div class="pb-2 pr-6 w-full lg:w-4/4">
                  <text-input
                    :key="index"
                    class="pb-2"
                    v-model="skuObj[`cuttings.${index}.type`]"
                    placeholder="Part Description"
                  />
                </div>
                <div 
                  v-if="skuObj[`cuttings.${index}.category`] == 'cushions'"
                  class="pb-2 pr-6 w-full lg:w-4/4"
                >
                  <select-input
                    v-model="cutting.quality"
                    class="pb-2"
                  >
                    <option :value="null">
                      Select Quality
                    </option>
                    <option
                      v-for="quality in qualities"
                      :key="index"
                      :value="quality.name"
                    >
                      {{ quality.name }}
                    </option>
                  </select-input>
                  <span
                    v-if="isValidationErrors(`cuttings.${index}.quality`)"
                    class="mt-3"
                  >
                    <span class="form-error">
                      {{ showValidationErrors(`cuttings.${index}.quality`) }}
                    </span>
                  </span>
                </div>
                <div 
                  v-if="skuObj[`cuttings.${index}.category`]== 'cushions'"
                  class="pb-2 pr-6 w-full lg:w-4/4"
                >
                  <select-input
                    v-model="cutting.discount_description_id"
                    class="pb-2"
                  >
                    <option :value="null">
                      Select Discount Descriptions
                    </option>
                    <option
                      v-for="discount_description in discount_descriptions"
                      :key="index"
                      :value="discount_description.id"
                    >
                      {{ discount_description.name }}
                    </option>
                  </select-input>
                  <span
                    v-if="isValidationErrors(`cuttings.${index}.discount_description_id`)"
                    class="mt-3"
                  >
                    <span class="form-error">
                      {{ showValidationErrors(`cuttings.${index}.discount_description_id`) }}
                    </span>
                  </span>
                </div>
                <div class="pb-2 pr-6 w-full lg:w-4/4">
                  <text-input
                    :key="index"
                    class="pb-2"
                    v-model="cutting.quantity"
                    :error="form.errors.quantity"
                    placeholder="Quantity"
                  />
                  <span
                    v-if="isValidationErrors(`cuttings.${index}.quantity`)"
                    class="mt-3"
                  >
                    <span class="form-error">{{ showValidationErrors(`cuttings.${index}.quantity`) }}</span>
                  </span>
                </div>
                <div class="pb-2 pr-6 w-full lg:w-4/4">
                  <text-input
                    :key="index"
                    class="pb-2"
                    v-model="cutting.weight"
                    :error="form.errors.weight"
                    placeholder="Weight"
                  />
                  <span
                    v-if="isValidationErrors(`cuttings.${index}.weight`)"
                    class="mt-3"
                  >
                    <span class="form-error">{{ showValidationErrors(`cuttings.${index}.weight`) }}</span>
                  </span>
                </div>
                <div class="pb-2 pr-6 w-full lg:w-4/4">
                  <div class="w-full lg:w-1/2">
                    <svg
                      v-show="form.cuttings.length > 1"
                      @click="removeCutting(index, form.cuttings)"
                      xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" style="color: red;">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div class="w-full lg:w-1/2">
                    <svg
                      v-show="index === countCuttings"
                      @click="addCutting"
                      xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div class="pb-2 pr-6 w-full lg:w-full">
                <hr />
              </div>
              <div class="flex flex-wrap -mb-2 -mr-6">
                <div class="pb-2 pr-6 w-full lg:w-1/2">
                  <text-input
                    v-model="form.recycled_part_number"
                    :error="form.errors.recycled_part_number"
                    placeholder="Part Number"
                    class="pb-2"
                  />
                </div>
                <div class="pb-2 pr-6 w-full lg:w-1/2">
                  <text-input
                    v-model="form.recycled_weight"
                    :error="form.errors.recycled_weight"
                    placeholder="Weight"
                    class="pb-2"
                  />
                </div>
              </div>
              <div class="pb-2 pr-6 w-full lg:w-full">
                <hr />
              </div>
            </div>
            <div
                class="flex items-center justify-end w-full px-8 py-4 bg-gray-50 border-t border-gray-100"
                >
                <loading-button
                    :loading="form.processing"
                    class="btn-orange px-3 py-2 text-xs"
                    type="submit"
                >
                    Update Output
                </loading-button>
                </div>
          </div>
        </form>
      </div>
      <hr/>
    </div>
  </template>
  
  <script>
  import { Head, Link } from '@inertiajs/inertia-vue3'
  import Layout from '@/Shared/Layout'
  import mapValues from 'lodash/mapValues'
  import DateRange from '@/Shared/DateRange'
  import TextInput from '@/Shared/TextInput'
  import SelectInput from '@/Shared/SelectInput'
  import LoadingButton from '@/Shared/LoadingButton'
  import Pagination from '@/Shared/Pagination'
  import Checkbox from '@/Shared/Checkbox'
  import axios from 'axios'
  
  export default {
    components: {
      Head,
      Link,
      TextInput,
      SelectInput,
      LoadingButton,
      DateRange,
      Pagination,
      Checkbox,
    },
    layout: Layout,
    props: {
      filters: Object,
      cuttings: Object,
      lines: Object,
      qualities: Object,
      discount_descriptions: Object,
      cutting_outputs: Object,
      block: Object,
      data: Array,
      isRejected: Boolean,
    },
    data() {
      return {
        is_rejected: this.isRejected,
        form: this.$inertia.form({
            block_number: this.block.name,
            size: null,
            volume: null,
            sku_volume: null,
            total_cut_weight: 1,
            cutting_output_volume: 1,
            line: this.block.line_id,
            dry_block_weight: this.block.dry_block_weight,
            cuttings: this.cuttings,
            recycled_part_number: this.block.recycled_part_number,
            recycled_weight: this.block.recycled_weight,
            form_view: 'edit',
        }),
        userSearching: false,
        yieldVolume: 0,
        skuObj: this.data,
      }
    },
    computed: {
      countCuttings() {
        if (this.form.cuttings.length === 0) {
          return 0;
        }
        return this.form.cuttings.length - 1;
      },
      role() {
        return this.$inertia.page.props.auth.user.role
      },
    },
    methods: {
      addCutting(e) {
        e.preventDefault();
        this.form.cuttings.push({
            form_view: null,
            output_id: null,
            item_id: null,
            cutting_size: null,
            category: null,
            discounted: null,
            quantity: null,
            quality: null,
            discount_description_id: null,
        });
      },
      isValidationErrors(value) {
        if (this.form.errors) {
          const error = this.form.errors[value] || '';
          return !!error;
        }
        return false;
      },
      showValidationErrors(value) {
        if (this.form.errors) {
          const error = this.form.errors[value] || '';
          return error || '';
        }
        return '';
      },
      sumQuantity() {
        axios.post(`/cutting/yield/search`, this.form).then(response => {
          this.yieldVolume = response.data
        })
      },
      removeCutting(index, cuttings) {
        cuttings.splice(index, 1)
      },
      reset() {
        this.search = mapValues(this.search, () => null)
      },
      update() {
        this.form.transform(({...data }) => ({
          is_rejected: this.is_rejected,
          ...data,
        }))
        this.form.post(`/blocks/${this.block.id}/cutting`, {
          onSuccess: () => {
            this.form.reset(
              'item_id',
              'block_number',
              'size',
              'cuttings',
              'volume',
              'cutting_size',
              'recycled_part_number',
              'recycled_weight',
              'dry_block_weight'
            )
          }
        });
      },
      checkUID() {
        this.userSearching = true
        axios.post(`/cutting/skus/search`, this.form).then(response => {
          this.skuObj = response.data
        })
      },
    },
  }
  </script>
  